import { useContext, useEffect, useState } from 'react'
import { StatisticsContext } from '../../../context/StatisticsContext'
import StatisticsService from '../../../services/StatisticsService'
import { statisticsColors } from '../StatisticsPage'
import CustomPaper from '../../../containers/Paper'
import { Bar, Pie } from 'react-chartjs-2'
import { CircularProgress } from '@mui/material'
import CustomDropdown from '../../../components/Dropdown'

interface DataType {
    jobTitle: string
    count: number
}

const CommonAreas = ({ type }: { type: 'bar' | 'pie' }) => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [data, setData] = useState<DataType[]>()
    const [roleNumbers, setRoleNumbers] = useState<string>('10')

    useEffect(() => {
        new StatisticsService()
            .getCommonAreas(carreer, startDate, endDate, roleNumbers)
            .then((response) => {
                if (response.status !== 200) return
                setData(response.data)
            })
    }, [carreer, startDate, endDate, roleNumbers])

    const getChartData = () => {
        return {
            labels: data?.map((x) => x.jobTitle),
            datasets: [
                {
                    label: type === 'bar' ? 'Cantidad de graduados' : 'Porcentaje de graduados',
                    data: data?.map((x) => x.count ?? 0),
                    backgroundColor: statisticsColors.background,
                    borderColor: statisticsColors.border,
                    borderWidth: 1,
                },
            ],
        }
    }

    const options = {
        scales:
            type === 'bar'
                ? {
                      y: {
                          beginAtZero: true,
                          ticks: { stepSize: 5 },
                      },
                      x: {
                          ticks: {
                              callback: function (index: any) {
                                  const label = data?.at(index)?.jobTitle ?? ''
                                  return label.length > 15 ? label.slice(0, 12) + '...' : label
                              },
                          },
                      },
                  }
                : undefined,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: type === 'pie', labels: { boxWidth: 13 } },
            tooltip: {
                callbacks: {
                    label(context: any) {
                        const label: string = context.dataset.label
                        const value = context.raw
                        const percentage = Number(
                            (
                                (100 * value) /
                                data!.map((x) => x.count).reduce((a, b) => a + b, 0)
                            ).toFixed(2),
                        )
                        return type === 'bar'
                            ? `${label}: ${value} (${percentage}%)`
                            : `${label}: ${percentage}% (${value})`
                    },
                },
            },
        },
    }

    return (
        <CustomPaper className={`flex flex-col gap-2 h-full md:h-[400px] text-primary`}>
            <div className="flex w-full items-start gap-2 md:gap-0 justify-between">
                <span className="w-[calc(100vw-175px)] md:w-fit text-lg font-semibold">
                    {type === 'bar'
                        ? 'Áreas y rubros con mayor cantidad de graduados'
                        : 'Distribución por área o rubro'}
                </span>
                <CustomDropdown
                    className="w-[100px] h-[40px]"
                    label="Cantidad"
                    value={roleNumbers}
                    onChange={(e) => setRoleNumbers(e.target.value as string)}
                    data={['5', '10', '15', '20']}
                />
            </div>
            <div className="flex-1 flex justify-center items-center">
                {data ? (
                    type === 'bar' ? (
                        <Bar
                            data={getChartData()}
                            options={options}
                        />
                    ) : (
                        <Pie
                            data={getChartData()}
                            options={options}
                        />
                    )
                ) : (
                    <div className="w-full h-full flex justify-center items-center">
                        <CircularProgress
                            className="m-2 md:m-5 text-primary"
                            size={64}
                        />
                    </div>
                )}
            </div>
        </CustomPaper>
    )
}

export default CommonAreas
